import { ThunkAPI } from '../rootReducer';
import { ContentTypes, HttpError, HttpMethods, SERVICES } from '../../services/api';
import { AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';
import { basicApiAsyncThunkCreator } from '../../services/apiAsyncThunkCreator';
import { showErrorMessage } from '../notifications';
import { CadenceTypesValues } from '../../components/auth/proRegister/subscriptionTypes';
import { Features, isFeatureEnabled } from '../../featureToggles/features';

const baseRoute = 'accounts/upgrade-to-professional';

export interface IIssueProfessionalSubscriptionDTO {
  subscriptionCadence: CadenceTypesValues;
  numberOfUsers: number;
  // Use for Features.PromotionalCodes
  promoCode: string | undefined;
  // End of use for Features.PromotionalCodes
}

export interface IIssueProfessionalSubscriptionResponse {
  clientSecret: string;
}

export const issueProfessionalSubscription: AsyncThunk<IIssueProfessionalSubscriptionResponse, IIssueProfessionalSubscriptionDTO, ThunkAPI> = createAsyncThunk(
  'issue_pro_subscription',
  async (data: IIssueProfessionalSubscriptionDTO, thunk) => {
    try {
      if (!data.promoCode && isFeatureEnabled(Features.PromotionalCodes)) {
        delete data.promoCode;
      }
      return await basicApiAsyncThunkCreator<IIssueProfessionalSubscriptionResponse, IIssueProfessionalSubscriptionDTO>({
        endpoint: `${baseRoute}/create-subscription`,
        microservice: SERVICES.USERS_SERVICE,
        authenticated: true,
        method: HttpMethods.Post,
        contentType: ContentTypes.ApplicationJson
      })(data, thunk);
    } catch (err) {
      let errorMessage = 'An error occurred requesting your upgrade';
      if (err instanceof Error || err instanceof HttpError) {
        errorMessage = err.message || errorMessage;
      }
      thunk.dispatch(showErrorMessage(errorMessage));
      return thunk.rejectWithValue(errorMessage);
    }
  }
);

export const confirmUpgradeToProfessionalPayment: AsyncThunk<void, void, ThunkAPI> = createAsyncThunk(
  'confirm_pro_payment',
  async (_, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<void, void>({
        endpoint: `${baseRoute}/confirm-payment`,
        microservice: SERVICES.USERS_SERVICE,
        authenticated: true,
        method: HttpMethods.Post,
        contentType: ContentTypes.ApplicationJson
      })(_, thunk);
      thunk.dispatch(upgradeToProfessionalAccount());
      return response;
    } catch (err) {
      let errorMessage = 'Your payment could not be confirmed. Please contact support';
      if (err instanceof Error || err instanceof HttpError) {
        errorMessage = err.message || errorMessage;
      }
      thunk.dispatch(showErrorMessage(errorMessage));
      return thunk.rejectWithValue(errorMessage);
    }
  }
);

export const upgradeToProfessionalAccount: AsyncThunk<void, void, ThunkAPI> = createAsyncThunk(
  'upgrade_pro_account',
  async (_, thunk) => {
    try {
      return await basicApiAsyncThunkCreator<void, void>({
        endpoint: `${baseRoute}/upgrade-account`,
        microservice: SERVICES.USERS_SERVICE,
        authenticated: true,
        method: HttpMethods.Post,
        contentType: ContentTypes.ApplicationJson
      })(_, thunk);
    } catch (err) {
      let errorMessage = 'An error occurred while upgrading your account';
      if (err instanceof Error || err instanceof HttpError) {
        errorMessage = err.message || errorMessage;
      }
      thunk.dispatch(showErrorMessage(errorMessage));
      return thunk.rejectWithValue(errorMessage);
    }
  }
);
